import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PWAContext = createContext(null);

export const PWAProvider = ({ children }) => {
  // PWA Installation States
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // 'ios' | 'android' | 'desktop' | 'unknown'
  const [isNativeMode, setIsNativeMode] = useState(false);

  // Detect platform
  useEffect(() => {
    const detectPlatform = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      
      // iOS detection
      if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
        setPlatform('ios');
        return;
      }
      
      // Android detection
      if (/android/i.test(ua)) {
        setPlatform('android');
        return;
      }
      
      // Desktop detection (rough check)
      if (window.innerWidth >= 1024) {
        setPlatform('desktop');
        return;
      }
      
      setPlatform('unknown');
    };

    detectPlatform();
    window.addEventListener('resize', detectPlatform);
    return () => window.removeEventListener('resize', detectPlatform);
  }, []);

  // Check if running in standalone mode (installed PWA)
  useEffect(() => {
    const checkStandalone = () => {
      // Check display-mode media query (works for Android Chrome, Desktop)
      const standaloneMediaQuery = window.matchMedia('(display-mode: standalone)');
      const isStandaloneMode = standaloneMediaQuery.matches;
      
      // Check navigator.standalone for iOS Safari
      const isIOSStandalone = 'standalone' in navigator && navigator.standalone === true;
      
      // Check for fullscreen mode (some PWAs use this)
      const isFullscreen = window.matchMedia('(display-mode: fullscreen)').matches;
      
      // Check for minimal-ui (alternative PWA display mode)
      const isMinimalUI = window.matchMedia('(display-mode: minimal-ui)').matches;
      
      const installed = isStandaloneMode || isIOSStandalone || isFullscreen || isMinimalUI;
      
      setIsStandalone(installed);
      setIsPWAInstalled(installed);
      setIsNativeMode(installed);
      
      // Add class to body for CSS targeting
      if (installed) {
        document.body.classList.add('pwa-installed', 'native-mode');
        document.documentElement.classList.add('pwa-installed');
      } else {
        document.body.classList.remove('pwa-installed', 'native-mode');
        document.documentElement.classList.remove('pwa-installed');
      }
    };

    checkStandalone();

    // Listen for display-mode changes
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkStandalone();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // Check if previously dismissed
    const dismissed = localStorage.getItem('damyxia-pwa-install-dismissed');
    if (dismissed === 'true') {
      setInstallDismissed(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Handle successful installation
    window.addEventListener('appinstalled', () => {
      setIsPWAInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
      setIsNativeMode(true);
      document.body.classList.add('pwa-installed', 'native-mode');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Trigger PWA installation
  const installPWA = useCallback(async () => {
    if (!deferredPrompt) {
      // For iOS, we can't trigger install programmatically
      if (platform === 'ios') {
        return { success: false, reason: 'ios-manual' };
      }
      return { success: false, reason: 'no-prompt' };
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsPWAInstalled(true);
        setCanInstall(false);
        setIsNativeMode(true);
        return { success: true, outcome: 'accepted' };
      } else {
        return { success: false, outcome: 'dismissed' };
      }
    } catch (error) {
      console.error('Error installing PWA:', error);
      return { success: false, reason: 'error', error };
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, platform]);

  // Dismiss install prompt
  const dismissInstall = useCallback(() => {
    setInstallDismissed(true);
    localStorage.setItem('damyxia-pwa-install-dismissed', 'true');
  }, []);

  // Reset install dismissal (for testing or after a period)
  const resetInstallDismissal = useCallback(() => {
    setInstallDismissed(false);
    localStorage.removeItem('damyxia-pwa-install-dismissed');
  }, []);

  // Check if install button should be shown
  const shouldShowInstallButton = canInstall && !isPWAInstalled && !installDismissed;

  // Get iOS install instructions
  const getIOSInstallInstructions = useCallback(() => {
    const language = localStorage.getItem('damyxia-language') || 'es';
    if (language === 'es') {
      return {
        title: 'Instalar DamyxIA en iOS',
        steps: [
          'Toca el botón de Compartir (cuadrado con flecha hacia arriba)',
          'Desplázate hacia abajo y toca "Añadir a pantalla de inicio"',
          'Toca "Añadir" en la esquina superior derecha'
        ]
      };
    }
    return {
      title: 'Install DamyxIA on iOS',
      steps: [
        'Tap the Share button (square with arrow pointing up)',
        'Scroll down and tap "Add to Home Screen"',
        'Tap "Add" in the top right corner'
      ]
    };
  }, []);

  const value = {
    // States
    isPWAInstalled,
    isStandalone,
    canInstall,
    installDismissed,
    platform,
    isNativeMode,
    shouldShowInstallButton,
    
    // Actions
    installPWA,
    dismissInstall,
    resetInstallDismissal,
    getIOSInstallInstructions,
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};

// Custom hook to use PWA context
export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

// Simple hook for quick PWA status check
export const usePWAStatus = () => {
  const { isPWAInstalled, isNativeMode, platform, canInstall } = usePWA();
  return { isPWAInstalled, isNativeMode, platform, canInstall };
};

export default PWAContext;
